import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { company } from '@/mocks/company';

const navItems = [
  { label: 'Leistungen', to: '/leistungen' },
  { label: 'Referenzen', to: '/referenzen' },
  { label: 'Über uns', to: '/ueber-uns' },
  { label: 'Karriere', to: '/karriere' },
  { label: 'Kontakt', to: '/kontakt' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const solid = scrolled || open || location.pathname !== '/';
  const isActive = (to: string) => location.pathname === to;

  return (
    <>
      <header
        className={`fixed top-0 z-50 w-full transition-all duration-300 ${
          solid ? 'bg-background-100' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between py-4 px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="/assets/logo.webp"
              alt="Matthias Klob GmbH Logo"
              className="w-11 h-11 rounded-sm object-contain"
            />
            <div className="flex flex-col leading-none">
              <span className="font-display font-extrabold uppercase tracking-tight text-foreground-50 text-xl">
                {company.shortName}
              </span>
              <span className="font-display font-semibold uppercase tracking-[0.28em] text-foreground-400 text-[10px] mt-1">
                {company.legalForm}
              </span>
            </div>
          </Link>

          <nav className="hidden lg:flex items-center gap-8 text-[15px] font-medium text-foreground-100">
            {navItems.map((i) => (
              <Link
                key={i.to}
                to={i.to}
                className={`cursor-pointer transition-colors hover:text-primary-400 whitespace-nowrap ${
                  isActive(i.to) ? 'text-primary-400' : ''
                }`}
              >
                {i.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/kontakt"
              className="hidden lg:inline-flex items-center h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold text-sm cursor-pointer whitespace-nowrap transition-colors"
            >
              Projekt anfragen
            </Link>
            <a
              href={company.phoneHref}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-50 cursor-pointer"
              aria-label={`${company.phone} anrufen`}
            >
              <i className="ri-phone-line text-lg"></i>
            </a>
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-300 text-foreground-50 cursor-pointer"
              aria-label="Menü öffnen"
              aria-expanded={open}
            >
              <i className={`ri-${open ? 'close' : 'menu'}-line text-xl`}></i>
            </button>
          </div>
        </div>
      </header>

      <div
        className={`fixed inset-0 z-40 bg-background-50/70 lg:hidden transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={() => setOpen(false)}
        aria-hidden="true"
      ></div>

      <div
        className={`fixed top-0 right-0 z-40 h-full w-[85%] max-w-sm bg-background-100 border-l border-background-300/40 lg:hidden transform transition-transform duration-300 ease-out ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col px-6 pt-28 pb-8 overflow-y-auto">
          <nav className="flex flex-col">
            {navItems.map((i, idx) => (
              <Link
                key={i.to}
                to={i.to}
                style={{ transitionDelay: open ? `${120 + idx * 60}ms` : '0ms' }}
                className={`flex items-center gap-3 border-b border-background-300/30 py-4 text-lg font-semibold text-foreground-100 cursor-pointer transition-all duration-300 ease-out ${
                  open ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
                }`}
              >
                <span className="font-mono text-xs text-primary-400">0{idx + 1}</span>
                {i.label}
              </Link>
            ))}
          </nav>
          <Link
            to="/kontakt"
            style={{ transitionDelay: open ? `${120 + navItems.length * 60}ms` : '0ms' }}
            className={`mt-6 inline-flex items-center justify-center h-11 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 font-semibold cursor-pointer whitespace-nowrap transition-all duration-300 ease-out ${
              open ? 'translate-x-0 opacity-100' : 'translate-x-8 opacity-0'
            }`}
          >
            Projekt anfragen
          </Link>
        </div>
      </div>
    </>
  );
}